import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Filter, Plus, RefreshCw, Search, Ticket as TicketIcon, X, } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/AppShell';
import { BackdatedBadge } from '@/components/tickets/BackdatedBanner';
import { PriorityBadge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field, Input, Select } from '@/components/ui/Input';
import { EmptyState, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { formatRelative, formatWithZone, getViewerTimezone } from '@/lib/timeDisplay';
import { SavedViewsBar } from '@/pages/tickets/SavedViewsBar';
import { hasPerm, useAuthStore } from '@/store/auth';
import { t, t as tr } from '@/lib/i18n';
import { statusDisplay } from '@/lib/types';
const SORTABLE_KEYS = [
    'ticket_number', 'title', 'type', 'priority', 'status',
    'sla_resolution_due_at', 'created_at',
];
const isSortKey = (v) => !!v && SORTABLE_KEYS.includes(v);
const PAGE_SIZE = 50;
// Date-preset chips. Values are stable URL strings (?date_preset=last_7d).
export const DATE_PRESETS = [
    { value: '', label: 'All time' },
    { value: 'today', label: 'Today' },
    { value: 'last_7d', label: 'Last 7 days' },
    { value: 'last_30d', label: 'Last 30 days' },
    { value: 'this_month', label: 'This month' },
    { value: 'last_month', label: 'Last month' },
    { value: 'custom', label: 'Custom range' },
];
// Which date column the range bounds apply to. Matches the backend `date_field`
// Query enum. 'created' is the default (byte-identical to pre-change behaviour),
// 'visit_scheduled' bounds the effective visit schedule (work order, else the
// create-form date). 'activity' (Ticket.updated_at) was removed from the
// dropdown 2026-07-16 (owner request — confusing for the team); the backend
// still accepts it, and an old deep-link/saved URL keeps working via
// isDateField below.
export const DATE_FIELDS = [
    { value: 'created', label: 'Created date' },
    { value: 'visit_scheduled', label: 'Visit scheduled date' },
    { value: 'closed', label: 'Closed date' },
];
export function isDateField(v) {
    return v === 'created' || v === 'activity' || v === 'visit_scheduled' || v === 'closed';
}
/**
 * Resolve the active created-date filter into UTC ISO bounds for the API.
 *
 * Pure (exported for tests). `now` is injected so tests can pin the clock
 * without monkey-patching Date. Bounds are inclusive:
 *   - from: start-of-day in the viewer's local timezone
 *   - to:   end-of-day in the viewer's local timezone (23:59:59.999)
 * Returns ISO 8601 with a Z suffix (or null when no bound applies).
 *
 * `custom` uses dateFrom/dateTo (YYYY-MM-DD); presets ignore them.
 */
export function resolveCreatedDateRange(preset, dateFrom, dateTo, now) {
    const startOfDay = (d) => {
        const c = new Date(d);
        c.setHours(0, 0, 0, 0);
        return c;
    };
    const endOfDay = (d) => {
        const c = new Date(d);
        c.setHours(23, 59, 59, 999);
        return c;
    };
    const today = startOfDay(now);
    if (preset === 'today') {
        return { fromIso: today.toISOString(), toIso: endOfDay(now).toISOString() };
    }
    if (preset === 'last_7d') {
        const from = new Date(today);
        from.setDate(from.getDate() - 6); // includes today + 6 days back = 7
        return { fromIso: from.toISOString(), toIso: endOfDay(now).toISOString() };
    }
    if (preset === 'last_30d') {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return { fromIso: from.toISOString(), toIso: endOfDay(now).toISOString() };
    }
    if (preset === 'this_month') {
        const from = new Date(today.getFullYear(), today.getMonth(), 1);
        return { fromIso: from.toISOString(), toIso: endOfDay(now).toISOString() };
    }
    if (preset === 'last_month') {
        const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const to = endOfDay(new Date(today.getFullYear(), today.getMonth(), 0)); // 0th day of this month = last day of prev
        return { fromIso: from.toISOString(), toIso: to.toISOString() };
    }
    if (preset === 'custom') {
        // YYYY-MM-DD strings → viewer-local start/end of day.
        const parse = (ymd) => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd))
                return null;
            const parts = ymd.split('-').map(Number);
            const [y, m, d] = parts;
            // Reject impossible dates (regex permits 2026-13-99). new Date(2026, 12, 99)
            // would silently roll over, so validate the round-trip.
            const date = new Date(y, m - 1, d);
            if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
                return null;
            }
            return date;
        };
        const f = dateFrom ? parse(dateFrom) : null;
        const t = dateTo ? parse(dateTo) : null;
        return {
            fromIso: f ? startOfDay(f).toISOString() : null,
            toIso: t ? endOfDay(t).toISOString() : null,
        };
    }
    return { fromIso: null, toIso: null };
}
export function TicketsListPage() {
    const navigate = useNavigate();
    const me = useAuthStore((s) => s.me);
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    // Seed the filters from the URL so a drill-down link from Reports/Insights
    // (e.g. /tickets?status=open&priority=P1&client_id=…) lands pre-filtered, and
    // the view is bookmarkable. Absent params default to '' → unchanged behaviour.
    const [status, setStatus] = useState(searchParams.get('status') ?? '');
    // Hold-reason drill-down (2026-08-18): only meaningful when the Status
    // filter is On Hold — "awaiting customer" is the desk's chase list.
    const [holdReason, setHoldReason] = useState(searchParams.get('substatus') ?? '');
    const [priority, setPriority] = useState(searchParams.get('priority') ?? '');
    const [createdBy, setCreatedBy] = useState(searchParams.get('created_by') ?? '');
    const [clientId, setClientId] = useState(searchParams.get('client_id') ?? '');
    // Intake channel (Phase 2) — e.g. channel=portal = "client-raised tickets".
    const [channel, setChannel] = useState(searchParams.get('channel') ?? '');
    // SLA-breached drill-down from the Home card (owner-requested 2026-07-24).
    const [breached, setBreached] = useState(searchParams.get('breached') === '1');
    // Aging drill-down (owner-approved 2026-07-31): ?age=0_24h|1_3d|3_7d|gt_7d.
    const [ageBucket, setAgeBucket] = useState(searchParams.get('age') ?? '');
    // Created-date filter — preset chip (covers 90% of clicks) + optional
    // custom range when preset === 'custom'. Bounds are interpreted in the
    // viewer's timezone (start-of-day → end-of-day) and converted to UTC
    // before being sent.
    const [datePreset, setDatePreset] = useState(searchParams.get('date_preset') ?? '');
    const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') ?? '');
    const [dateTo, setDateTo] = useState(searchParams.get('date_to') ?? '');
    // Which date column the range applies to — defaults to 'created' which
    // preserves the current behaviour exactly when the URL has no `date_field`.
    const initialDateField = searchParams.get('date_field');
    const [dateField, setDateField] = useState(isDateField(initialDateField) ? initialDateField : 'created');
    // "Created By" + "Client" filter dropdowns — only for agents (users who can
    // update tickets), matching the assignable-users endpoint's own eligibility.
    // The list returns creator/assignee/closer names regardless; these are just
    // the filter controls. Customers/vendors get role-narrowed lists.
    const canFilterByCreator = hasPerm(me, 'ticket.update');
    const creatorsQ = useQuery({
        queryKey: ['assignable-users', 'ticket-creators'],
        queryFn: () => api.get('/admin/users/assignable'),
        enabled: canFilterByCreator,
        staleTime: 5 * 60_000,
    });
    const clientsQ = useQuery({
        queryKey: ['clients', 'ticket-filter'],
        queryFn: () => api.get('/clients'),
        enabled: canFilterByCreator,
        staleTime: 5 * 60_000,
    });
    // FTS search box. We keep two values:
    //   `qInput`  — what the user is currently typing (re-renders on every keystroke)
    //   `q`       — the debounced version sent to the API (re-fetches at most every 300ms)
    // This avoids hammering the backend while the user types but keeps the
    // input responsive. Initial value seeded from URL so /tickets?q=printer
    // restores the search on reload / back-button.
    const [qInput, setQInput] = useState(searchParams.get('q') ?? '');
    const [q, setQ] = useState(searchParams.get('q') ?? '');
    useEffect(() => {
        const t = setTimeout(() => setQ(qInput.trim()), 300);
        return () => clearTimeout(t);
    }, [qInput]);
    // Two-way sync for `channel` — the sidebar "Client Tickets" link navigates
    // to /tickets?channel=portal while this component may ALREADY be mounted
    // (e.g. coming from My Queue), and the useState initializer only seeds on
    // mount. Equality guards on both directions make the pair loop-safe.
    useEffect(() => {
        const urlChannel = searchParams.get('channel') ?? '';
        if (urlChannel !== channel)
            setChannel(urlChannel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (channel)
            next.set('channel', channel);
        else
            next.delete('channel');
        if (next.toString() !== searchParams.toString())
            setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);
    // Mirror the remaining filters into the URL too (2026-08-14, owner-approved):
    // they were mount-seeded from the URL but never written BACK, so opening a
    // ticket and pressing Back reloaded a bare /tickets and silently dropped
    // them. Same replace-in-place pattern as channel/q/date — Back now restores
    // the filtered view, and it becomes bookmarkable/shareable.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (status)
            next.set('status', status);
        else
            next.delete('status');
        if (priority)
            next.set('priority', priority);
        else
            next.delete('priority');
        if (createdBy)
            next.set('created_by', createdBy);
        else
            next.delete('created_by');
        if (clientId)
            next.set('client_id', clientId);
        else
            next.delete('client_id');
        if (breached)
            next.set('breached', '1');
        else
            next.delete('breached');
        if (ageBucket)
            next.set('age', ageBucket);
        else
            next.delete('age');
        if (status === 'on_hold' && holdReason)
            next.set('substatus', holdReason);
        else
            next.delete('substatus');
        if (next.toString() !== searchParams.toString())
            setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, priority, createdBy, clientId, breached, ageBucket, holdReason]);
    // Mirror the debounced search term into the URL so it's bookmarkable.
    // Replace history instead of push so each keystroke doesn't pollute the
    // back stack.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (q)
            next.set('q', q);
        else
            next.delete('q');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);
    // Mirror the date filter into the URL so a bookmarked /tickets?date_preset=…
    // (and the custom from/to + the date_field column) lands pre-filtered.
    // `date_field=created` is the default — keep it out of the URL so existing
    // bookmarks stay byte-identical.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (datePreset)
            next.set('date_preset', datePreset);
        else
            next.delete('date_preset');
        if (datePreset === 'custom' && dateFrom)
            next.set('date_from', dateFrom);
        else
            next.delete('date_from');
        if (datePreset === 'custom' && dateTo)
            next.set('date_to', dateTo);
        else
            next.delete('date_to');
        if (dateField && dateField !== 'created')
            next.set('date_field', dateField);
        else
            next.delete('date_field');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datePreset, dateFrom, dateTo, dateField]);
    // 1-indexed for UX. Server is offset-based via `skip`.
    const [page, setPage] = useState(1);
    // Sort state — read from URL so the agent can bookmark / share a sorted view.
    // Defaults: created_at DESC (newest first), matching pre-sort behaviour.
    const sortKeyParam = searchParams.get('sort');
    const sortDirParam = searchParams.get('dir');
    const sortBy = isSortKey(sortKeyParam) ? sortKeyParam : 'created_at';
    const sortDir = sortDirParam === 'asc' ? 'asc' : 'desc';
    // "My Queue" mode — sidebar link `/tickets?assignee=me` resolves to the
    // calling user's UUID at fetch time. Stable URL, no role-name leak.
    const assigneeMe = searchParams.get('assignee') === 'me';
    // `assignee=me` resolves to the caller; an explicit `assigned_engineer_id`
    // (e.g. an engineer-leaderboard drill-down) is honoured too.
    const explicitEngineerId = searchParams.get('assigned_engineer_id') ?? '';
    const assignedEngineerId = assigneeMe && me?.user.id ? me.user.id : explicitEngineerId || undefined;
    // Reset to Page 1 whenever any filter changes (status, priority, the
    // assignee-me URL flag, or the sort spec). Without this, switching from
    // Resolved (page 5 of 6) to New (1 page total) would show an empty page,
    // and re-sorting while on page 47 would silently drop the user past the
    // end of the new ordering.
    useEffect(() => {
        setPage(1);
    }, [status, priority, createdBy, clientId, channel, q, assigneeMe, sortBy, sortDir, datePreset, dateFrom, dateTo, dateField]);
    // Resolve the active date filter once per render. `Date.now()`/`new Date()`
    // are allowed in components (the ban is only in Workflow scripts).
    const { fromIso: createdFromIso, toIso: createdToIso } = resolveCreatedDateRange(datePreset, dateFrom, dateTo, new Date());
    const skip = (page - 1) * PAGE_SIZE;
    const params = new URLSearchParams();
    if (status)
        params.set('status', status);
    if (status === 'on_hold' && holdReason)
        params.set('substatus', holdReason);
    if (priority)
        params.set('priority', priority);
    if (createdBy)
        params.set('created_by', createdBy);
    if (clientId)
        params.set('client_id', clientId);
    if (channel)
        params.set('channel', channel);
    if (breached)
        params.set('breached', 'true');
    if (ageBucket)
        params.set('age_bucket', ageBucket);
    if (q)
        params.set('q', q);
    if (assignedEngineerId)
        params.set('assigned_engineer_id', assignedEngineerId);
    if (createdFromIso)
        params.set('created_from', createdFromIso);
    if (createdToIso)
        params.set('created_to', createdToIso);
    if (dateField !== 'created' && (createdFromIso || createdToIso)) {
        params.set('date_field', dateField);
    }
    params.set('skip', String(skip));
    params.set('limit', String(PAGE_SIZE));
    params.set('sort_by', sortBy);
    params.set('sort_dir', sortDir);
    const qs = `?${params.toString()}`;
    const list = useQuery({
        queryKey: ['tickets', { status, priority, createdBy, clientId, channel, breached, ageBucket, q, assignedEngineerId, createdFromIso, createdToIso, dateField, skip, PAGE_SIZE, sortBy, sortDir }],
        queryFn: () => api.get(`/tickets${qs}`),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: 20_000,
        placeholderData: (prev) => prev, // keep previous page visible during refetch
    });
    // Toast on transient failures so the agent doesn't have to scroll up to see
    // the inline rose alert. Fires once per error transition (not on every
    // 20-second poll while the error persists) by gating on isError + dataUpdatedAt.
    useEffect(() => {
        if (list.isError) {
            toast.error('Could not load tickets', list.error?.message ?? 'Try the Retry button or refresh the page.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list.isError, list.errorUpdatedAt]);
    // Click a column header to cycle sort: unsorted → asc → desc → unsorted.
    // We model "unsorted" as the default (created_at DESC).
    const onSortClick = (key) => {
        const next = new URLSearchParams(searchParams);
        if (sortBy !== key) {
            // First click on a new column → ascending.
            next.set('sort', key);
            next.set('dir', 'asc');
        }
        else if (sortDir === 'asc') {
            next.set('sort', key);
            next.set('dir', 'desc');
        }
        else {
            // Third click on the same column → revert to default ordering.
            next.delete('sort');
            next.delete('dir');
        }
        setSearchParams(next, { replace: true });
    };
    const totalCount = list.data?.total_count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const items = list.data?.items ?? [];
    // Clamp page if filter change drops the total below current page.
    useEffect(() => {
        if (page > totalPages)
            setPage(totalPages);
    }, [page, totalPages]);
    const dateFilterActive = !!(createdFromIso || createdToIso);
    const activeFilters = [status, priority, channel, q].filter(Boolean).length + (dateFilterActive ? 1 : 0);
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: assigneeMe ? t('My Queue') : t('Tickets'), description: assigneeMe
                    ? t('Tickets currently assigned to you.')
                    : t('All tickets in your tenant scope (RLS-filtered).'), actions: _jsxs(_Fragment, { children: [assigneeMe && (_jsxs(Button, { variant: "ghost", onClick: () => {
                                const next = new URLSearchParams(searchParams);
                                next.delete('assignee');
                                setSearchParams(next);
                            }, title: "Clear My Queue filter", children: [_jsx(X, { size: 14 }), t('Show all tickets')] })), _jsxs(Button, { variant: "ghost", onClick: async () => {
                                const r = await list.refetch();
                                if (r.isError) {
                                    toast.error(tr('Refresh failed'), r.error?.message);
                                }
                                // No success toast: a successful refresh is the silent default.
                                // Auto-fetch every 20 s would otherwise spam toasts.
                            }, disabled: list.isFetching, title: "Refresh now", children: [_jsx(RefreshCw, { size: 16, className: list.isFetching ? 'animate-spin' : undefined }), list.isFetching ? t('Refreshing…') : t('Refresh')] }), _jsx(Link, { to: "/tickets/new", children: _jsxs(Button, { children: [_jsx(Plus, { size: 16 }), t('New ticket')] }) })] }) }), _jsxs(PageBody, { children: [breached && (_jsx("div", { className: "mb-2 flex items-center gap-2", children: _jsxs("span", { className: "inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300", children: ["SLA breached only", _jsx("button", { type: "button", "aria-label": t('Clear SLA-breached filter'), className: "hover:text-rose-900 dark:hover:text-rose-100", onClick: () => {
                                        setBreached(false);
                                        const next = new URLSearchParams(searchParams);
                                        next.delete('breached');
                                        setSearchParams(next, { replace: true });
                                    }, children: "\u2715" })] }) })), _jsx(SavedViewsBar, {}), _jsx(Card, { className: "mb-4", children: _jsxs(CardBody, { children: [_jsxs("div", { className: "mb-3 flex items-center gap-2", children: [_jsx(Filter, { className: "h-4 w-4 text-slate-400" }), _jsx("span", { className: "text-xs font-semibold uppercase tracking-wider text-slate-500", children: "Filters" }), activeFilters > 0 && (_jsxs("span", { className: "ml-auto rounded-full bg-brand-100 px-2 py-0.5 text-tag text-brand-700 dark:bg-brand-950/60 dark:text-brand-300", children: [activeFilters, " active"] }))] }), _jsxs("div", { className: "grid gap-3 md:grid-cols-3", children: [_jsx(Field, { label: t('Search'), children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" }), _jsx(Input, { value: qInput, onChange: (e) => setQInput(e.target.value), placeholder: t('title or description…'), className: "pl-8 pr-7" }), qInput && (_jsx("button", { type: "button", "aria-label": t('Clear search'), onClick: () => setQInput(''), className: "absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800", children: _jsx(X, { size: 12 }) }))] }) }), _jsx(Field, { label: t('Status'), children: _jsxs(Select, { value: breached ? '__breached' : status, onChange: (e) => {
                                                    const v = e.target.value;
                                                    // URL-synced so Back from a ticket returns to the same
                                                    // filtered list (owner-approved 2026-07-31).
                                                    const next = new URLSearchParams(searchParams);
                                                    if (v === '__breached') {
                                                        setBreached(true);
                                                        setStatus('');
                                                        next.set('breached', '1');
                                                        next.delete('status');
                                                    }
                                                    else {
                                                        setBreached(false);
                                                        setStatus(v);
                                                        next.delete('breached');
                                                        if (v)
                                                            next.set('status', v);
                                                        else
                                                            next.delete('status');
                                                    }
                                                    setSearchParams(next, { replace: true });
                                                }, children: [_jsx("option", { value: "", children: t('All statuses') }), ['new', 'open', 'in_progress', 'on_hold', 'resolved', 'closed', 'cancelled'].map((s) => (_jsx("option", { value: s, children: statusDisplay(s) }, s))), _jsx("option", { value: "__breached", children: t('SLA Breached') })] }) }), status === 'on_hold' && (_jsx(Field, { label: t('Hold reason'), children: _jsxs(Select, { value: holdReason, onChange: (e) => setHoldReason(e.target.value), children: [_jsx("option", { value: "", children: t('All hold reasons') }), _jsx("option", { value: "scheduled", children: t('Visit scheduled (later)') }), _jsx("option", { value: "waiting_customer", children: t('Awaiting customer') }), _jsx("option", { value: "waiting_vendor", children: t('Awaiting vendor') }), _jsx("option", { value: "waiting_parts", children: t('Awaiting parts') })] }) })), _jsx(Field, { label: t('Age'), hint: t('Open tickets by time since creation (matches the Aging report).'), children: _jsxs(Select, { value: ageBucket, onChange: (e) => {
                                                    const v = e.target.value;
                                                    setAgeBucket(v);
                                                    const next = new URLSearchParams(searchParams);
                                                    if (v)
                                                        next.set('age', v);
                                                    else
                                                        next.delete('age');
                                                    setSearchParams(next, { replace: true });
                                                }, children: [_jsx("option", { value: "", children: t('All ages') }), _jsx("option", { value: "0_24h", children: "0\u201324h" }), _jsx("option", { value: "1_3d", children: "1\u20133d" }), _jsx("option", { value: "3_7d", children: "3\u20137d" }), _jsx("option", { value: "gt_7d", children: ">7d" })] }) }), _jsx(Field, { label: t('Priority'), children: _jsxs(Select, { value: priority, onChange: (e) => setPriority(e.target.value), children: [_jsx("option", { value: "", children: t('All priorities') }), ['P1', 'P2', 'P3', 'P4'].map((p) => (_jsx("option", { value: p, children: p }, p)))] }) }), _jsx(Field, { label: t('Channel'), hint: t('Portal = raised by clients through the self-service portal.'), children: _jsxs(Select, { value: channel, onChange: (e) => setChannel(e.target.value), children: [_jsx("option", { value: "", children: t('All channels') }), _jsx("option", { value: "portal", children: t('Portal (client-raised)') }), _jsx("option", { value: "manual", children: t('Manual (service desk)') }), _jsx("option", { value: "email", children: t('Email') }), _jsx("option", { value: "api", children: t('API') })] }) }), canFilterByCreator && (_jsx(Field, { label: t('Created By'), children: _jsxs(Select, { value: createdBy, onChange: (e) => setCreatedBy(e.target.value), children: [_jsx("option", { value: "", children: t('All creators') }), (creatorsQ.data ?? []).map((u) => (_jsx("option", { value: u.id, children: u.display_name }, u.id)))] }) })), canFilterByCreator && (_jsx(Field, { label: t('Client'), children: _jsxs(Select, { value: clientId, onChange: (e) => setClientId(e.target.value), children: [_jsx("option", { value: "", children: t('All clients') }), (clientsQ.data ?? []).map((c) => (_jsx("option", { value: c.id, children: c.name }, c.id)))] }) })), _jsxs(Field, { label: t('Date filter'), children: [_jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsx(Select, { value: dateField, onChange: (e) => {
                                                                const v = e.target.value;
                                                                if (isDateField(v))
                                                                    setDateField(v);
                                                            }, "aria-label": t('Date field'), title: "Which date column the range applies to", children: DATE_FIELDS.map((f) => (_jsx("option", { value: f.value, children: f.label }, f.value))) }), _jsx(Select, { value: datePreset, onChange: (e) => {
                                                                setDatePreset(e.target.value);
                                                                if (e.target.value !== 'custom') {
                                                                    setDateFrom('');
                                                                    setDateTo('');
                                                                }
                                                            }, "aria-label": t('Date range'), children: DATE_PRESETS.map((p) => (_jsx("option", { value: p.value, children: p.label }, p.value))) })] }), datePreset === 'custom' && (_jsxs("div", { className: "mt-2 grid grid-cols-2 gap-2", children: [_jsx(Input, { type: "date", value: dateFrom, max: dateTo || undefined, onChange: (e) => setDateFrom(e.target.value), "aria-label": t('From date') }), _jsx(Input, { type: "date", value: dateTo, min: dateFrom || undefined, onChange: (e) => setDateTo(e.target.value), "aria-label": t('To date') })] }))] })] })] }) }), list.isLoading && (_jsx("div", { className: "space-y-2", children: Array.from({ length: 5 }).map((_, i) => (_jsx("div", { className: "h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800/50" }, i))) })), list.isError && !list.isLoading && (_jsxs("div", { className: "flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0" }), _jsxs("span", { children: ["Couldn't load tickets:\u00A0", _jsx("code", { className: "font-mono text-xs", children: list.error?.message ?? 'unknown error' }), _jsx("button", { onClick: () => list.refetch(), className: "ml-3 underline hover:text-rose-900", children: t('Retry') })] })] })), list.data && items.length === 0 && (_jsx(EmptyState, { icon: _jsx(TicketIcon, { className: "h-6 w-6" }), message: activeFilters > 0 ? t('No tickets match your filters.') : t('No tickets yet — create your first one.'), cta: _jsx(Link, { to: "/tickets/new", children: _jsxs(Button, { children: [_jsx(Plus, { size: 16 }), t('Create ticket')] }) }) })), list.data && items.length > 0 && (() => {
                        // "Missing metadata" — a ticket is an orphan only when it truly
                        // lacks the field. Site: the field-service create wizard stores the
                        // on-site location as a typed address inside
                        // custom_fields_json.field_service.site (the linked Site master is
                        // deliberately optional, §33.1) — so EITHER a linked site_id OR a
                        // captured on-site address counts as "has a site". Before this fix
                        // (2026-07-14) every wizard-created ticket was flagged "no site"
                        // even though the address was right there on the ticket.
                        const fsSite = (t) => {
                            const fs = t.custom_fields_json?.field_service;
                            return fs?.site ?? {};
                        };
                        const hasSite = (t) => {
                            if (t.site_id)
                                return true;
                            const site = fsSite(t);
                            return Boolean((typeof site.full_address === 'string' && site.full_address.trim()) ||
                                (typeof site.city === 'string' && site.city.trim()) ||
                                (typeof site.country === 'string' && site.country.trim()));
                        };
                        const isOrphanTicket = (t) => !t.client_id || !hasSite(t) || !t.service_id;
                        const orphans = items.filter(isOrphanTicket).length;
                        return (_jsxs(_Fragment, { children: [orphans > 0 && (_jsxs("div", { className: "mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0" }), _jsxs("div", { children: [_jsx("strong", { children: orphans }), " ticket", orphans === 1 ? ' is' : 's are', " missing client, site, or service metadata (older submissions). They are still listed below \u2014 open each to triage and add the missing fields."] })] })), _jsxs(Table, { children: [_jsx(THead, { children: _jsxs(TR, { children: [_jsx(SortableTH, { label: t('Number'), col: "ticket_number", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('Title'), col: "title", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('Type'), col: "type", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('Priority'), col: "priority", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('Status'), col: "status", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('SLA Resolution'), col: "sla_resolution_due_at", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(SortableTH, { label: t('Created'), col: "created_at", sortBy: sortBy, sortDir: sortDir, onSort: onSortClick }), _jsx(TH, { children: t('Visit date') }), _jsx(TH, { children: t('Created By') }), _jsx(TH, { children: t('Assigned To') }), _jsx(TH, { children: t('Closed By') })] }) }), _jsx(TBody, { children: items.map((t) => {
                                                // Defensive coercion — never let a malformed row disappear silently.
                                                const num = t.ticket_number || `(no #) ${t.id.slice(0, 8)}…`;
                                                const title = t.title || '(no title)';
                                                const tType = t.type || 'unknown';
                                                const tStatus = t.status || 'new';
                                                const tags = Array.isArray(t.tags) ? t.tags : [];
                                                // Show every time in the viewer's zone WITH an explicit label
                                                // so a multi-country team is never guessing which clock it is.
                                                const viewerTz = getViewerTimezone();
                                                const createdLabel = formatWithZone(t.created_at, viewerTz);
                                                const createdTooltip = formatRelative(t.created_at, viewerTz).tooltip;
                                                const slaLabel = t.sla_resolution_due_at
                                                    ? formatWithZone(t.sla_resolution_due_at, viewerTz, { showAbbrev: true })
                                                    : '—';
                                                // "Visit date" (2026-07-16): the dispatched work-order time
                                                // (viewer tz, like Created), else the visit date typed on
                                                // the create form (shown as typed — it's a site-local
                                                // calendar date, converting could shift a midnight).
                                                const fsSched = t.custom_fields_json?.field_service;
                                                const wizardDate = fsSched?.scheduling?.date_of_visit;
                                                const visitLabel = t.visit_scheduled_at
                                                    ? formatWithZone(t.visit_scheduled_at, viewerTz)
                                                    : wizardDate
                                                        ? `${wizardDate}${fsSched?.scheduling?.time_of_visit ? ` ${fsSched.scheduling.time_of_visit}` : ''}`
                                                        : '—';
                                                const visitTooltip = t.visit_scheduled_at
                                                    ? 'Dispatched work-order time (your timezone)'
                                                    : wizardDate
                                                        ? 'Intended visit date from the create form (not yet dispatched)'
                                                        : 'No visit planned';
                                                const isOrphan = isOrphanTicket(t);
                                                return (_jsxs(TR, { onClick: () => navigate(`/tickets/${t.id}`), children: [_jsx(TD, { className: "font-mono text-xs font-medium text-brand-600 dark:text-brand-400", children: num }), _jsxs(TD, { children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-medium text-slate-900 dark:text-slate-100", children: title }), isOrphan && (_jsxs("span", { className: "inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-tag text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900", title: [
                                                                                !t.client_id ? 'no client' : null,
                                                                                !hasSite(t) ? 'no site or on-site address' : null,
                                                                                !t.service_id ? 'no service' : null,
                                                                            ].filter(Boolean).join(' · '), children: [_jsx(AlertTriangle, { size: 10 }), "Missing metadata"] })), _jsx(BackdatedBadge, { occurredAt: t.occurred_at ?? null, createdAt: t.created_at, reasonCode: t.backdate_reason_code ?? null })] }), tags.length > 0 && (_jsx("div", { className: "mt-1.5 flex flex-wrap gap-1", children: tags.map((tag) => (_jsx("span", { className: "rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400", children: tag }, tag))) }))] }), _jsx(TD, { className: "text-xs uppercase tracking-wide text-slate-500", children: tType }), _jsx(TD, { children: t.priority ? _jsx(PriorityBadge, { priority: t.priority }) : _jsx("span", { className: "text-xs text-slate-400", children: "\u2014" }) }), _jsx(TD, { children: _jsx(StatusBadge, { status: tStatus, substatus: t.substatus }) }), _jsxs(TD, { className: "text-xs text-slate-500", children: [slaLabel, t.sla_resolution_breached && (_jsx("span", { className: "ml-2 inline-flex items-center rounded-md bg-rose-50 px-1.5 py-0.5 text-tag text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900", children: "BREACHED" }))] }), _jsx(TD, { className: "text-xs text-slate-500", children: _jsx("span", { title: createdTooltip, children: createdLabel }) }), _jsx(TD, { className: "text-xs text-slate-500", children: _jsx("span", { title: visitTooltip, children: visitLabel }) }), _jsx(TD, { className: "text-xs text-slate-600 dark:text-slate-300", children: t.created_by_display_name || '—' }), _jsx(TD, { className: "text-xs text-slate-600 dark:text-slate-300", children: t.assigned_engineer_display_name || '—' }), _jsx(TD, { className: "text-xs text-slate-600 dark:text-slate-300", children: t.closed_by_display_name || '—' })] }, t.id));
                                            }) })] }), totalPages > 1 && (_jsxs("div", { className: "flex flex-col items-end gap-2 border-t border-slate-100 px-4 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between", children: [_jsxs("span", { children: ["Showing ", _jsx("strong", { className: "text-slate-700 dark:text-slate-200", children: skip + 1 }), "\u2013", _jsx("strong", { className: "text-slate-700 dark:text-slate-200", children: Math.min(skip + items.length, totalCount) }), ' ', "of ", _jsx("strong", { className: "text-slate-700 dark:text-slate-200", children: totalCount.toLocaleString() }), " tickets"] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: () => setPage((p) => Math.max(1, p - 1)), disabled: page <= 1 || list.isFetching, children: [_jsx(ChevronLeft, { size: 14 }), "Previous"] }), _jsxs("span", { className: "px-2 font-mono text-slate-700 dark:text-slate-200", children: ["Page ", page, " of ", totalPages] }), _jsxs(Button, { size: "sm", variant: "outline", onClick: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: page >= totalPages || list.isFetching, children: ["Next", _jsx(ChevronRight, { size: 14 })] })] })] }))] }));
                    })()] })] }));
}
/**
 * Sortable column header — wraps the existing <TH> primitive with a button
 * that cycles asc → desc → unsorted on click. Keyboard-accessible (Enter/
 * Space activate the button), and announces sort state via aria-sort so
 * screen-reader users hear "ascending" / "descending" / "none".
 *
 * Visual cue:
 *   - Inactive column: faint up/down arrow.
 *   - Active asc:      bold up arrow, indigo.
 *   - Active desc:     bold down arrow, indigo.
 */
function SortableTH({ label, col, sortBy, sortDir, onSort, }) {
    const active = sortBy === col;
    const ariaSort = !active ? 'none' : (sortDir === 'asc' ? 'ascending' : 'descending');
    const Icon = !active ? ArrowUpDown
        : sortDir === 'asc' ? ArrowUp
            : ArrowDown;
    return (_jsx(TH, { ariaSort: ariaSort, scope: "col", children: _jsxs("button", { type: "button", onClick: () => onSort(col), className: [
                'group inline-flex items-center gap-1 select-none',
                'rounded -mx-1 px-1 py-0.5',
                'hover:text-slate-900 dark:hover:text-slate-100',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                active ? 'text-brand-700 dark:text-brand-300' : '',
            ].join(' '), "aria-label": `Sort by ${label}, currently ${ariaSort}`, children: [label, _jsx(Icon, { className: [
                        'h-3 w-3 transition-opacity',
                        active ? 'opacity-100' : 'opacity-40 group-hover:opacity-70',
                    ].join(' '), "aria-hidden": "true" })] }) }));
}
